import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { HelpQueriesView } from '@shared-ui/help-queries';
import type { Thread, ThreadApiClient } from '@shared-ui/threads';
import { type UserSearchOption } from '@shared-ui/user-picker';
import { ScopedUserSearchFilter } from '../../components/ScopedUserSearchFilter';
import { adminApi } from '../../api/adminApi';
import { authApi } from '../../api/auth';
import { adminThreadApi } from '../../api/adminThreadApi';
import { fetchWithAdmin } from '../../api/fetchHelper';

/** `dd MMM yyyy` — the doubt list wants the day, not the minute. */
function formatDate(iso: string | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

const adminApiClient: ThreadApiClient = {
    fetchApi: <T,>(endpoint: string, options?: RequestInit) => fetchWithAdmin<T>(endpoint, options),
};

export function AdminHelpQueries() {
    const navigate = useNavigate();
    const admin = authApi.getUser();

    const { data: usersData } = useQuery({
        queryKey: ['admin-users', { pageSize: 500 }],
        queryFn: () => adminApi.getUsers({ pageSize: 500 }),
        staleTime: 5 * 60 * 1000,
    });

    // Teachers are fetched separately, by role. The 500-user page above cannot
    // be relied on to contain them — prod has ~1.5k users, so a thread's owner
    // routinely fell outside it and the Teacher column rendered a truncated id
    // ("e0f3c777…") instead of a name. Students still resolve from that page,
    // and fall back to the message sender name when they don't.
    const { data: teachersData } = useQuery({
        queryKey: ['admin-users', { role: 'teacher', pageSize: 500 }],
        queryFn: () => adminApi.getUsers({ role: 'teacher', pageSize: 500 }),
        staleTime: 5 * 60 * 1000,
    });

    const userMap = useMemo(() => {
        const map = new Map<string, { name: string; email: string }>();
        (usersData?.items || []).forEach(u => map.set(u.id, { name: u.name, email: u.email }));
        // Teachers last so a full record wins over a partial one.
        (teachersData?.items || []).forEach(u => map.set(u.id, { name: u.name, email: u.email }));
        return map;
    }, [usersData, teachersData]);

    const teachers = useMemo(
        () => (teachersData?.items || []).slice().sort((a, b) => a.name.localeCompare(b.name)),
        [teachersData]
    );

    const [student, setStudent] = useState<UserSearchOption | null>(null);
    const [studentEmailDraft, setStudentEmailDraft] = useState('');
    const [studentApplied, setStudentApplied] = useState<string>('');
    const [teacherDraft, setTeacherDraft] = useState('');
    const [teacherApplied, setTeacherApplied] = useState('');

    const createdByFilter = studentApplied || undefined;
    const ownerIdFilter = teacherApplied || undefined;

    const renderUserLink = (userId: string | undefined, fallback: string) => {
        if (!userId) return fallback;
        const u = userMap.get(userId);
        const label = u?.name || `${userId.substring(0, 8)}...`;
        return (
            <a
                href={`/users/${userId}`}
                className="hq-user-link"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/users/${userId}`); }}
            >
                {label}
            </a>
        );
    };

    return (
        <HelpQueriesView
            threadApi={adminThreadApi}
            role="admin"
            listFilters={{ type: 'student_teacher' }}
            showKindFilter
            queryKey={['admin-all-help-queries']}
            currentUserId={admin?.id}
            canReply={false}
            canChangeStatus={false}
            markSeenOnView={false}
            participantLabel="Student"
            title="Student–Teacher Messages"
            detailTitle="Student Doubt"
            getParticipant={(t) => {
                const u = userMap.get(t.createdBy);
                return u?.name || t.messages[0]?.senderName || '—';
            }}
            getCreatorName={(t) => {
                const u = userMap.get(t.createdBy);
                return u ? `${u.name} (${u.email})` : (t.messages[0]?.senderName || '—');
            }}
            leadingColumns={[
                {
                    // `created` is the key the view's sorter understands for
                    // createdAt — see the fix that introduced it.
                    key: 'created',
                    header: 'Date',
                    render: (t: Thread) => <span className="hq-time">{formatDate(t.createdAt)}</span>,
                    width: '110px',
                    sortable: true,
                },
            ]}
            extraColumns={[
                {
                    key: 'teacher',
                    header: 'Teacher',
                    render: (t: Thread) => renderUserLink(t.ownerId, '—'),
                    width: '130px',
                },
            ]}
            // The built-in Created On sits behind six wider columns and never
            // comes into view at laptop widths; Date above replaces it.
            hideCreated
            onNavigateToObject={(type, id) => {
                if (type === 'question') navigate(`/questions?id=${id}`);
                if (type === 'submission') navigate(`/submissions/${id}`);
                if (type === 'assignment_attempt') navigate(`/questionsessionreport/by-session/${id}`);
            }}
            extraListFilters={{
                ...(createdByFilter ? { createdBy: createdByFilter } : {}),
                ...(ownerIdFilter ? { ownerId: ownerIdFilter } : {}),
            }}
            extraFilterKey={`${studentApplied}|${teacherApplied}`}
            onFetchClick={() => {
                setStudentApplied(student?.userId ?? '');
                setTeacherApplied(teacherDraft);
            }}
            extraFilterUI={
                <>
                    {/* Search-and-select rather than a free-text email: the old
                        input needed an exact address and silently matched
                        nothing otherwise. Picking resolves the id directly. */}
                    <ScopedUserSearchFilter
                        value={studentEmailDraft}
                        onChange={(v) => {
                            setStudentEmailDraft(v);
                            if (!v) setStudent(null);   // cleared box = no student filter
                        }}
                        onPick={setStudent}
                        placeholder="Search student…"
                    />
                    <select
                        className="sfb-select"
                        value={teacherDraft}
                        onChange={(e) => setTeacherDraft(e.target.value)}
                    >
                        <option value="">All Teachers</option>
                        {teachers.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </>
            }
            apiClient={adminApiClient}
            loadSubmission={async (submissionId) => {
                const sub = await adminApi.getSubmission(submissionId);
                const fb = sub.feedback;
                return {
                    id: sub.id,
                    submittedAt: sub.submittedAt,
                    timeTakenSeconds: sub.timeTakenSeconds,
                    solutionImageUrl: sub.canvasImageUrl,
                    typedText: sub.typedText,
                    score: fb?.score,
                    correctnessScore: fb?.computedScores?.correctnessScore,
                    correctnessScoreComment: fb?.computedScores?.correctnessScoreComment,
                    approachScore: fb?.computedScores?.approachScore,
                    approachScoreComment: fb?.computedScores?.approachScoreComment,
                    speedScore: fb?.computedScores?.speedScore,
                    speedScoreComment: fb?.computedScores?.speedScoreComment,
                };
            }}
            renderStudentDetail={(thread) => {
                const u = userMap.get(thread.createdBy);
                if (!u) {
                    return <div className="hq-student-info"><div className="hq-student-field"><span className="hq-student-field-value">—</span></div></div>;
                }
                return (
                    <div className="hq-student-info">
                        <div className="hq-student-field">
                            <span className="hq-student-field-label">Name</span>
                            <span className="hq-student-field-value">
                                <a
                                    href={`/users/${thread.createdBy}`}
                                    className="hq-user-link"
                                    onClick={(e) => { e.preventDefault(); navigate(`/users/${thread.createdBy}`); }}
                                >
                                    {u.name}
                                </a>
                            </span>
                        </div>
                        <div className="hq-student-field">
                            <span className="hq-student-field-label">Email</span>
                            <span className="hq-student-field-value">{u.email}</span>
                        </div>
                    </div>
                );
            }}
            renderTeacherDetail={(thread) => {
                const t = thread.ownerId ? userMap.get(thread.ownerId) : undefined;
                if (!t) {
                    return (
                        <div className="hq-student-info">
                            <div className="hq-student-field"><span className="hq-student-field-value">—</span></div>
                        </div>
                    );
                }
                return (
                    <div className="hq-student-info">
                        <div className="hq-student-field">
                            <span className="hq-student-field-label">Name</span>
                            <span className="hq-student-field-value">
                                <a
                                    href={`/users/${thread.ownerId}`}
                                    className="hq-user-link"
                                    onClick={(e) => { e.preventDefault(); navigate(`/users/${thread.ownerId}`); }}
                                >
                                    {t.name}
                                </a>
                            </span>
                        </div>
                        <div className="hq-student-field">
                            <span className="hq-student-field-label">Email</span>
                            <span className="hq-student-field-value">{t.email}</span>
                        </div>
                    </div>
                );
            }}
            emptyMessage="No help queries in the system."
        />
    );
}
